/**
 * A statement's check script on the account's Rules page (§5.27) — the app's
 * field (`rules-check-field.tsx` there): its path in the rules repository
 * (`checks/<name>.py`) and its text, as the draft or the branch holds it,
 * proposed with the RFC by the same pull request. An audit runs it in the
 * session's machine in place of the auditor reading for the statement.
 */
import { useState } from 'react';
import type React from 'react';
import { CHECK_FILE } from '@airlock/contracts/rules/repository';
import { GhostButton, Input } from '../../../components/primitives';
import { color, font } from '../../../theme/tokens';
import { textareaStyle } from '../shared';

/** What a new script starts from: the contract, on a pattern to replace — the app's own template. */
export const CHECK_TEMPLATE = [
  'import sys',
  '',
  "# Each argument is a file of the change, from its repository's root.",
  '# Exit 0 when they honour the statement; print one `path:line: what is wrong` per finding and exit 1.',
  'found = []',
  'for path in sys.argv[1:]:',
  '    with open(path, encoding="utf-8", errors="replace") as f:',
  '        for n, line in enumerate(f, 1):',
  '            if "TODO" in line:  # what the statement forbids',
  '                found.append(f"{path}:{n}: what the code does, and the fix")',
  'if found:',
  '    print("\\n".join(found))',
  'sys.exit(1 if found else 0)',
  ''
].join('\n');

export function CheckFields({
  slug,
  check,
  error,
  script,
  onCheck,
  onScript,
  Field
}: {
  slug: string;
  check: string | undefined;
  error: string | null;
  /** The text written here, else the draft's or the branch's — undefined when none holds one. */
  script: string | undefined;
  onCheck: (check: string | undefined) => void;
  onScript: (path: string, text: string) => void;
  Field: (props: { label: string; hint?: string; error: string | null; children: React.ReactNode }) => React.JSX.Element;
}): React.JSX.Element {
  // Shown once asked for: an empty path field reads as one already filled by its placeholder.
  const [adding, setAdding] = useState(false);
  if (!check && !adding) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <GhostButton
          size="sm"
          onClick={() => {
            setAdding(true);
            onCheck(`checks/${slug || 'check'}.py`);
          }}
        >
          Add a check script
        </GhostButton>
        <span style={{ fontSize: 12.5, color: color.inkFaint }}>A Python script decides this statement instead of the auditor.</span>
      </div>
    );
  }
  const valid = !!check && CHECK_FILE.test(check);
  return (
    <>
      <Field
        label="Check"
        error={error}
        hint="A Python script of the rules repository decides this statement in the session’s machine — the auditor does not read for it."
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input
            value={check ?? ''}
            placeholder="checks/<name>.py"
            onChange={(event) => onCheck(event.target.value.trim() || undefined)}
            style={{ fontFamily: font.mono, fontSize: 13, flex: 1 }}
          />
          <GhostButton
            size="sm"
            onClick={() => {
              setAdding(false);
              onCheck(undefined);
            }}
          >
            Remove check
          </GhostButton>
        </div>
      </Field>
      {valid && (
        <Field
          label="Script"
          error={null}
          hint="Run as python3 -I <script> <file>… from the repository’s root: exit 0 holds, 1 with one path:line: finding a line. Anything else is a check that did not run."
        >
          <textarea
            aria-label={`Script ${check}`}
            value={script ?? ''}
            rows={12}
            spellCheck={false}
            placeholder="No script at this path yet."
            onChange={(event) => onScript(check, event.target.value)}
            style={{ ...textareaStyle, fontFamily: font.mono, fontSize: 12.5, whiteSpace: 'pre', overflowWrap: 'normal' }}
          />
        </Field>
      )}
      {valid && !script && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: color.inkFaint }}>{check} is not in the rules repository yet.</span>
          <GhostButton size="sm" onClick={() => onScript(check, CHECK_TEMPLATE)}>
            Start from the template
          </GhostButton>
        </div>
      )}
    </>
  );
}
